import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as Clock } from '@pkg/assets/icons/v2/clock.svg';
import { DepButton } from '@pkg/components/deprecated';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { type ApiResult, type TransfersResponse, TransferStatus } from '@app/client';
import { getCurrentTransferStatus } from '@app/helpers';
import { addNotification } from '@app/store/actions/notification';
import { cancelTransfer, setTransferData, setTransferPanelOpen } from '@app/store/actions/transfers';
import { useEmojisStore } from '@app/store/zustand/emojis/useEmojisStore';

import '../YatTransferSheet.scss';

export function CancelTransfer() {
    const [translate] = useTranslation(['transfers', 'translation', 'words']);
    const { outgoingTransfers } = useAppSelector((state) => state.transfers);
    const currentEmoji = useEmojisStore((state) => state.currentEmoji);

    const dispatch = useAppDispatch();

    const cancel = useCallback(() => {
        const id = getCurrentTransferStatus({ eid: currentEmoji, outgoingTransfers }).id;
        const dispatchedPromise = dispatch(cancelTransfer(id) as unknown) as Promise<ApiResult<TransfersResponse>>;

        dispatchedPromise
            .then((r) => {
                if (r.ok) {
                    dispatch(
                        addNotification({
                            message: translate('cancel_transfer_alert_message'),
                            severity: 'alert',
                            title: translate('cancel_transfer_alert_title'),
                        }),
                    );
                    dispatch(setTransferData(currentEmoji, { status: TransferStatus.Deleted }));
                } else {
                    dispatch(addNotification({ message: 'Error', severity: 'error' }));
                }
            })
            .catch((e) => {
                if (e) {
                    dispatch(addNotification({ message: e.message, severity: 'error' }));
                }
            });
    }, [currentEmoji, outgoingTransfers, dispatch, translate]);

    const handleNeverMind = useCallback(() => {
        dispatch(setTransferPanelOpen(false));
        dispatch(setTransferData(currentEmoji, { status: TransferStatus.Pending }));
    }, [currentEmoji, dispatch]);

    return (
        <div className="yat-transfer-sidebar__step">
            <div className="yat-transfer-sidebar__flex-item">
                <div className="yat-transfer-sidebar__title-holder">
                    <h1 className="yat-transfer-sidebar__title">{translate('cancel_transfer_title')}</h1>
                    <Clock className="yat-transfer-sidebar__title-icon" />
                </div>

                <p className="yat-transfer-sidebar__copy v2-text-fluid">{translate('cancel_transfer_body_copy')}</p>
            </div>

            <div className="yat-transfer-sidebar__flex-item">
                <div className="yat-transfer-sidebar__button-spacer">
                    <DepButton
                        disabled={false}
                        variant="primary"
                        size={'large'}
                        classes={'yat-transfer-sidebar__button-50'}
                        onClick={() => handleNeverMind()}
                    >
                        {translate('words:nevermind')}
                    </DepButton>

                    <DepButton
                        classes={'yat-transfer-sidebar__button-50'}
                        disabled={false}
                        variant="caution"
                        size={'large'}
                        onClick={() => cancel()}
                    >
                        {translate('cancel_transfer_cta_copy')}
                    </DepButton>
                </div>
            </div>
        </div>
    );
}
